import {useCallback, useEffect, useRef, useState} from 'react';
import {twMerge} from '../../../common/twMerge';
import {
  ANIMATION_DURATION,
  IGNORE_GLOBAL_FONT_SET_CLASSNAME,
  TYPEWRITE_EFFECT_SPEED,
} from '../../../domain/content/[seriesId]/viewer/[productId]/constant/talkViewerConstant';
import {FontLoadStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/FontLoadStatus.enum';
import {TalkEffectType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkEffectType.enum';
import {TalkMediaStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkMediaStatus.enum';
import {TalkViewerFontFamilyType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkViewerFontFamilyType.enum';
import {calculateTalkViewerSize} from '../../../domain/content/[seriesId]/viewer/[productId]/util/calculateTalkViewerSize';
import {setMediaCurrentTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/setMediaCurrentTime';
import {theme} from '../../../theme';
import {TalkEffectProps} from './TalkEffectProps.interface';

export function TalkEffect({
  effectType,
  scripts,
  font,
  audioUrl,
  mediaStatus,
  muted,
  isShowingTalk,
  needMediaPlayedDirect,
  onAudioEnded,
  fontLoadStatus,
}: TalkEffectProps) {
  const talkEffectRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [talkEffectHeight, setTalkEffectHeight] = useState(0);
  const [typewriterIndex, setTypewriterIndex] = useState(0);
  const typingTimer = useRef(0);
  useEffect(() => {
    if (
      !talkEffectRef.current ||
      fontLoadStatus === FontLoadStatus.BeforeLoad
    ) {
      return;
    }
    setTalkEffectHeight(talkEffectRef.current.clientHeight);
  }, [fontLoadStatus, isShowingTalk]);
  useEffect(() => {
    if (!talkEffectRef.current) {
      return;
    }
    if (!isShowingTalk) {
      talkEffectRef.current.style.animationName = 'none';
      // 기존 진행되던 timer는 정리 및 type index 초기화
      window.clearInterval(typingTimer.current);
      setTypewriterIndex(0);
      return;
    }

    if (effectType === TalkEffectType.Typing) {
      let index = 0;

      typingTimer.current = window.setInterval(() => {
        if (scripts.length > index) {
          setTypewriterIndex(index++);
        } else {
          window.clearInterval(typingTimer.current);
        }
      }, TYPEWRITE_EFFECT_SPEED);
    } else if (effectType) {
      talkEffectRef.current.style.animationName = effectType;
    }

    return () => window.clearTimeout(typingTimer.current);
  }, [effectType, scripts.length, isShowingTalk]);

  useEffect(() => {
    if (!mediaStatus || !audioRef.current) {
      return;
    }
    if (mediaStatus === TalkMediaStatus.Play) {
      if (needMediaPlayedDirect) {
        return;
      }
      audioRef.current.play();
    } else {
      setMediaCurrentTime(audioRef.current, 0);
      audioRef.current.pause();
    }
  }, [mediaStatus, needMediaPlayedDirect]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.muted = muted;
  }, [muted]);

  const onClickSound = useCallback(() => {
    if (!audioRef.current || muted || !needMediaPlayedDirect) {
      return;
    }
    audioRef.current.paused && audioRef.current.play();
  }, [muted, needMediaPlayedDirect]);

  if (!effectType) {
    return <>{scripts}</>;
  }
  return (
    <div
      className="relative overflow-hidden"
      style={{
        height: talkEffectHeight ? talkEffectHeight : 'auto',
      }}
      onClick={onClickSound}
    >
      <div
        ref={talkEffectRef}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          'talk-effect-container',
          IGNORE_GLOBAL_FONT_SET_CLASSNAME,
          'relative box-border overflow-hidden p-15tvs text-30tvs font-bold leading-[1.76]'
        )}
        style={{
          letterSpacing: theme.spacing['5tvs'],
          animationName: effectType,
          animationDuration: `${ANIMATION_DURATION[effectType]}s`,
          fontSize: font.size
            ? calculateTalkViewerSize(font.size)
            : theme.spacing['30tvs'],
          color: font.color,
        }}
      >
        {audioUrl && (
          <audio
            src={audioUrl}
            controls={false}
            ref={audioRef}
            onEnded={onAudioEnded}
            muted={muted}
          />
        )}
        {effectType === TalkEffectType.Typing && talkEffectHeight
          ? scripts.slice(0, typewriterIndex + 1)
          : scripts}
      </div>
      <style jsx>{`
        .talk-effect-container {
          font-family: ${font.family || TalkViewerFontFamilyType.Default} !important;
        }
      `}</style>
      <style jsx global>{`
        @keyframes ${TalkEffectType.Shake} {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          60% {
            transform: scale(1);
          }
          70% {
            transform: scale(1.1);
          }
          80% {
            transform: scale(1);
          }
          90% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes ${TalkEffectType.FadeIn} {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes ${TalkEffectType.Rise} {
          0% {
            top: ${talkEffectHeight}px;
          }
          100% {
            top: 0;
          }
        }

        @keyframes ${TalkEffectType.Flick} {
          0% {
            opacity: 1;
          }
          10% {
            opacity: 0;
          }
          20% {
            opacity: 1;
          }
          30% {
            opacity: 0;
          }
          40% {
            opacity: 1;
            transform: scale(1);
          }
          100% {
            opacity: 0;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
}
